/**
 * Central Change History store — system-generated, immutable entries.
 * Local Audit Logs and the Change History page read from this same store.
 */

import {
  buildChangeHistoryEntry,
  formatChangedOnDisplay,
  permissionModuleForHistoryEntry,
  toLocalAuditLogRow,
  CHANGE_HISTORY_DISPLAY,
} from './changeHistoryEntry';
import {
  CHANGE_HISTORY_SEED_ENTRIES,
  CHANGE_HISTORY_SEED_SEQUENCE,
} from './changeHistorySeedData';

/** Bumped when default seed coverage expands so browsers reload illustrative rows. */
const STORAGE_KEY = 'tp_change_history_v2';
const SEQUENCE_KEY = 'tp_change_history_seq_v2';

/** Illustrative historical examples only — not live Rules, roles or settings. */
const SEED_ENTRIES = CHANGE_HISTORY_SEED_ENTRIES;

let memoryEntries = null;
let memorySeq = null;
const listeners = new Set();

function readStorage() {
  if (typeof window === 'undefined' || !window.localStorage) {
    return { entries: [...SEED_ENTRIES], seq: CHANGE_HISTORY_SEED_SEQUENCE };
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const seqRaw = window.localStorage.getItem(SEQUENCE_KEY);
    if (!raw) {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(SEED_ENTRIES));
      window.localStorage.setItem(SEQUENCE_KEY, String(CHANGE_HISTORY_SEED_SEQUENCE));
      return { entries: [...SEED_ENTRIES], seq: CHANGE_HISTORY_SEED_SEQUENCE };
    }
    const parsed = JSON.parse(raw);
    const entries = Array.isArray(parsed) ? parsed : [...SEED_ENTRIES];
    const seq = Number(seqRaw) || CHANGE_HISTORY_SEED_SEQUENCE;
    return { entries, seq };
  } catch {
    return { entries: [...SEED_ENTRIES], seq: CHANGE_HISTORY_SEED_SEQUENCE };
  }
}

function ensureLoaded() {
  if (memoryEntries) return;
  const { entries, seq } = readStorage();
  memoryEntries = entries;
  memorySeq = seq;
}

function persist() {
  ensureLoaded();
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(memoryEntries));
    window.localStorage.setItem(SEQUENCE_KEY, String(memorySeq));
  } catch {
    /* ignore quota */
  }
}

function notify() {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch {
      /* ignore */
    }
  });
}

function nextChangeId() {
  ensureLoaded();
  memorySeq += 1;
  return `CHG-${String(memorySeq).padStart(6, '0')}`;
}

export function subscribeChangeHistory(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getAllChangeHistoryEntries({ includeDeleted = false } = {}) {
  ensureLoaded();
  const list = includeDeleted
    ? [...memoryEntries]
    : memoryEntries.filter((entry) => !entry.deleted);
  return list.sort((a, b) => {
    const first = new Date(a.changedOnIso).getTime();
    const second = new Date(b.changedOnIso).getTime();
    if (Number.isNaN(first) || Number.isNaN(second)) return 0;
    return second - first;
  });
}

export function findChangeHistoryBySourceRef(sourceChangeRef) {
  ensureLoaded();
  if (!sourceChangeRef) return null;
  return memoryEntries.find((entry) => entry.sourceChangeRef === sourceChangeRef && !entry.deleted) || null;
}

/**
 * Append one permanent history entry. Repeated delivery of the same
 * sourceChangeRef returns the existing entry (no duplicate).
 */
export function appendChangeHistoryEntry(input) {
  ensureLoaded();
  const draft = buildChangeHistoryEntry(input);
  const existing = findChangeHistoryBySourceRef(draft.sourceChangeRef);
  if (existing) return existing;

  const entry = {
    ...draft,
    changeId: draft.changeId || nextChangeId(),
  };
  memoryEntries = [entry, ...memoryEntries];
  persist();
  notify();
  return entry;
}

/**
 * Public recording helper for source modules after a successful save/activity.
 */
export function recordChangeHistory(input) {
  if (!input) return null;
  return appendChangeHistoryEntry(input);
}

export function getChangeHistoryForSource({
  sourceModuleRef,
  sourceItemRef,
  accountRef,
  ticketRef,
  caseRef,
} = {}) {
  return getAllChangeHistoryEntries().filter((entry) => {
    if (sourceModuleRef && entry.sourceModuleRef !== sourceModuleRef) return false;
    if (sourceItemRef && entry.sourceItemRef !== String(sourceItemRef)) return false;
    if (accountRef && entry.accountRef !== String(accountRef) && entry.sourceItemRef !== String(accountRef)) {
      return false;
    }
    if (ticketRef && entry.ticketRef !== String(ticketRef) && entry.sourceItemRef !== String(ticketRef)) {
      return false;
    }
    if (caseRef && entry.caseRef !== String(caseRef) && entry.sourceItemRef !== String(caseRef)) {
      return false;
    }
    return Boolean(sourceModuleRef || sourceItemRef || accountRef || ticketRef || caseRef);
  });
}

export function getLocalAuditLogRows(scope) {
  return getChangeHistoryForSource(scope).map(toLocalAuditLogRow).filter(Boolean);
}

/**
 * Filter entries visible to the current viewer.
 * Owner/Co-owner / full access / explicit change_history without module limits → all.
 * Ordinary Team Member: needs change_history view + originating module view.
 */
export function filterChangeHistoryForAccess(entries, accessContext = {}) {
  const { hasFullAccess, hasModuleView, isOwnerOrCoOwner } = accessContext;
  if (isOwnerOrCoOwner || hasFullAccess) return entries;

  const canViewHistory = typeof hasModuleView === 'function' ? hasModuleView('change_history') : true;
  if (!canViewHistory) return [];

  return entries.filter((entry) => {
    const required = permissionModuleForHistoryEntry(entry);
    if (!required) return true;
    if (typeof hasModuleView !== 'function') return true;
    return hasModuleView(required);
  });
}

/**
 * Retention final action — deletes only the history entry after all checks permit.
 * Does not delete the source record. Does not create another history entry for the removal.
 */
export function deleteChangeHistoryEntryPermanently(changeId, { allowed = false } = {}) {
  if (!allowed || !changeId) return { success: false, message: 'Retention checks not satisfied.' };
  ensureLoaded();
  const index = memoryEntries.findIndex((entry) => entry.changeId === changeId);
  if (index < 0) return { success: false, message: 'Entry not found.' };
  memoryEntries = memoryEntries.filter((entry) => entry.changeId !== changeId);
  persist();
  notify();
  return { success: true };
}

export function resetChangeHistoryStoreForTests() {
  memoryEntries = [...SEED_ENTRIES];
  memorySeq = CHANGE_HISTORY_SEED_SEQUENCE;
  persist();
  notify();
}

export {
  SEED_ENTRIES,
  formatChangedOnDisplay,
  toLocalAuditLogRow,
  buildChangeHistoryEntry,
  CHANGE_HISTORY_DISPLAY,
};

export {
  sanitiseFieldValue,
  sanitiseValuesMap,
  formatChangeValues,
  permissionModuleForHistoryEntry,
} from './changeHistoryEntry';

/** Retention ownership remains Configuration — Retention; Change History has no local editor. */
export const CHANGE_HISTORY_RETENTION_OWNER = 'Configuration — Retention';
